import * as Cannon from "cannon-es";
import * as Three from "three";

Three.ColorManagement.enabled = false;

type LegacyModel = {
  vertices: number[];
  faces: number[];
  normals?: number[];
};

class LegacyJsonLoader {
  parse(model: LegacyModel): { geometry: Three.BufferGeometry } {
    const geometry = new Three.BufferGeometry();
    const positions: number[] = [];
    const normals: number[] = [];
    const faces = model.faces;
    const sourceNormals = model.normals ?? [];
    let offset = 0;

    const appendVertex = (vertexIndex: number, normalIndex?: number) => {
      positions.push(
        model.vertices[vertexIndex * 3]!,
        model.vertices[(vertexIndex * 3) + 1]!,
        model.vertices[(vertexIndex * 3) + 2]!,
      );
      if (normalIndex !== undefined) {
        normals.push(
          sourceNormals[normalIndex * 3]!,
          sourceNormals[(normalIndex * 3) + 1]!,
          sourceNormals[(normalIndex * 3) + 2]!,
        );
      }
    };

    while (offset < faces.length) {
      const type = faces[offset++] ?? 0;
      const isQuad = (type & 1) !== 0;
      const vertexCount = isQuad ? 4 : 3;
      const vertices = faces.slice(offset, offset + vertexCount);
      offset += vertexCount;

      if (type & 2) offset += 1;
      if (type & 4) offset += 1;
      if (type & 8) offset += vertexCount;
      const faceNormal = type & 16 ? faces[offset++] : undefined;
      const vertexNormals = type & 32 ? faces.slice(offset, offset + vertexCount) : [];
      if (type & 32) offset += vertexCount;
      if (type & 64) offset += 1;
      if (type & 128) offset += vertexCount;

      const normalAt = (index: number) => faceNormal ?? vertexNormals[index];
      const triangleIndices = isQuad ? [0, 1, 3, 1, 2, 3] : [0, 1, 2];
      for (const index of triangleIndices) appendVertex(vertices[index]!, normalAt(index));
    }

    geometry.setAttribute("position", new Three.Float32BufferAttribute(positions, 3));
    if (normals.length === positions.length) {
      geometry.setAttribute("normal", new Three.Float32BufferAttribute(normals, 3));
    } else {
      geometry.computeVertexNormals();
    }
    return { geometry };
  }
}

const legacyLambertVertexShader = `
uniform vec3 diffuse;
uniform vec3 ambient;
uniform vec3 emissive;
uniform vec3 ambientLightColor;

#if NUM_POINT_LIGHTS > 0
struct PointLight {
  vec3 position;
  vec3 color;
  float distance;
  float decay;
};
uniform PointLight pointLights[NUM_POINT_LIGHTS];
#endif

#if NUM_HEMI_LIGHTS > 0
struct HemisphereLight {
  vec3 direction;
  vec3 skyColor;
  vec3 groundColor;
};
uniform HemisphereLight hemisphereLights[NUM_HEMI_LIGHTS];
#endif

varying vec3 vLegacyLightFront;
#include <fog_pars_vertex>

void main() {
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  vec3 transformedNormal = normalize(normalMatrix * normal);
  vec3 lightFront = vec3(0.0);

  #if NUM_POINT_LIGHTS > 0
  for (int i = 0; i < NUM_POINT_LIGHTS; i++) {
    vec3 lightVector = pointLights[i].position - mvPosition.xyz;
    float lightDistance = 1.0;
    if (pointLights[i].distance > 0.0) {
      lightDistance = 1.0 - min(length(lightVector) / pointLights[i].distance, 1.0);
    }
    lightVector = normalize(lightVector);
    lightFront += pointLights[i].color * max(dot(transformedNormal, lightVector), 0.0) * lightDistance;
  }
  #endif

  #if NUM_HEMI_LIGHTS > 0
  for (int i = 0; i < NUM_HEMI_LIGHTS; i++) {
    float hemiWeight = 0.5 * dot(transformedNormal, normalize(hemisphereLights[i].direction)) + 0.5;
    lightFront += mix(hemisphereLights[i].groundColor, hemisphereLights[i].skyColor, hemiWeight);
  }
  #endif

  vLegacyLightFront = lightFront * diffuse + ambient * ambientLightColor + emissive;
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}
`;

const legacyLambertFragmentShader = `
uniform float opacity;
varying vec3 vLegacyLightFront;
#include <fog_pars_fragment>

void main() {
  gl_FragColor = vec4(vLegacyLightFront, opacity);
  #include <fog_fragment>
}
`;

class LegacyMeshLambertMaterial extends Three.ShaderMaterial {
  constructor(parameters: Record<string, unknown> = {}) {
    const legacyColor = new Three.Color((parameters.color ?? 0xffffff) as Three.ColorRepresentation);
    const opacity = typeof parameters.opacity === "number" ? parameters.opacity : 1;
    const modernParameters = { ...parameters } as Record<string, unknown>;
    delete modernParameters.color;
    delete modernParameters.shading;
    super({
      ...modernParameters,
      lights: true,
      fog: parameters.fog !== false,
      toneMapped: false,
      uniforms: Three.UniformsUtils.merge([
        Three.UniformsLib.lights,
        Three.UniformsLib.fog,
        {
          diffuse: { value: legacyColor },
          ambient: { value: new Three.Color(0xffffff) },
          emissive: { value: new Three.Color(0x000000) },
          opacity: { value: opacity },
        },
      ]),
      vertexShader: legacyLambertVertexShader,
      fragmentShader: legacyLambertFragmentShader,
    });
  }
}

class LegacyMeshBasicMaterial extends Three.MeshBasicMaterial {
  constructor(parameters: Three.MeshBasicMaterialParameters = {}) {
    const modernParameters = { ...parameters };
    if (modernParameters.color !== undefined) {
      modernParameters.color = new Three.Color(modernParameters.color).convertSRGBToLinear();
    }
    super(modernParameters);
  }
}

class LegacyHemisphereLight extends Three.HemisphereLight {
  constructor(skyColor?: Three.ColorRepresentation, groundColor?: Three.ColorRepresentation, intensity = 1) {
    super(skyColor, groundColor, intensity);
  }
}

class LegacyRigidBody extends Cannon.Body {
  constructor(mass: number, shape: Cannon.Shape, material?: Cannon.Material) {
    super({ mass, shape, material });
    (this as unknown as { shape: Cannon.Shape }).shape = shape;
    (this as unknown as { index: number }).index = this.id;
  }
}

class LegacyRay {
  private readonly origin: Cannon.Vec3;
  private readonly direction: Cannon.Vec3;

  constructor(origin: { x: number; y: number; z: number }, direction: { x: number; y: number; z: number }) {
    this.origin = new Cannon.Vec3(origin.x, origin.y, origin.z);
    this.direction = new Cannon.Vec3(direction.x, direction.y, direction.z);
  }

  intersectBody(body: Cannon.Body): Array<{ body: Cannon.Body; distance: number; point: Cannon.Vec3 }> {
    const endpoint = new Cannon.Vec3(
      this.origin.x + (this.direction.x * 100_000),
      this.origin.y + (this.direction.y * 100_000),
      this.origin.z + (this.direction.z * 100_000),
    );
    const ray = new Cannon.Ray(this.origin, endpoint);
    ray.mode = Cannon.Ray.CLOSEST;
    ray.checkCollisionResponse = false;
    const result = new Cannon.RaycastResult();
    ray.intersectBody(body, result);
    if (!result.hasHit || !result.body) return [];
    return [{ body: result.body, distance: result.distance, point: result.hitPointWorld.clone() }];
  }
}

class LegacyContactMaterial extends Cannon.ContactMaterial {
  constructor(
    materialA: Cannon.Material,
    materialB: Cannon.Material,
    friction = 0,
    restitution = 0,
  ) {
    super(materialA, materialB, { friction, restitution });
  }
}

class LegacyHingeConstraint extends Cannon.HingeConstraint {
  constructor(
    bodyA: Cannon.Body,
    pivotA: Cannon.Vec3,
    axisA: Cannon.Vec3,
    bodyB: Cannon.Body,
    pivotB: Cannon.Vec3,
    axisB: Cannon.Vec3,
  ) {
    super(bodyA, bodyB, { pivotA, axisA, pivotB, axisB });
  }
}

const threeCompat = {
  ...Three,
  CubeGeometry: Three.BoxGeometry,
  JSONLoader: LegacyJsonLoader,
  MeshBasicMaterial: LegacyMeshBasicMaterial,
  MeshLambertMaterial: LegacyMeshLambertMaterial,
  HemisphereLight: LegacyHemisphereLight,
  FlatShading: true,
  VertexColors: true,
};

const cannonCompat = {
  ...Cannon,
  RigidBody: LegacyRigidBody,
  ContactMaterial: LegacyContactMaterial,
  HingeConstraint: LegacyHingeConstraint,
  Ray: LegacyRay,
};

const worldPrototype = Cannon.World.prototype as Cannon.World & {
  add: (body: Cannon.Body) => void;
  remove: (body: Cannon.Body) => void;
  collisionMatrixSet?: () => void;
};
worldPrototype.add = Cannon.World.prototype.addBody;
worldPrototype.remove = Cannon.World.prototype.removeBody;
worldPrototype.collisionMatrixSet = () => undefined;

Object.assign(globalThis, { THREE: threeCompat, CANNON: cannonCompat });

async function loadClassicScript(source: string): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = source;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${source}`));
    document.head.appendChild(script);
  });
}

const scripts = [
  "/js/libs/detector.min.js",
  "/js/game/game.static.js",
  "/js/game/game.three.js",
  "/js/game/game.cannon.js",
  "/js/game/game.events.js",
  "/js/game/game.helpers.js",
  "/js/game/game.ui.js",
  "/js/game/game.core.js",
  "/js/game/init.js",
];

for (const script of scripts) await loadClassicScript(script);
